import React, { createContext, useEffect, useState } from "react";
import * as notify from "shared/notify";
import SelectReferences from "../selectReferences";
import { useAttributesForReferences } from "shared/hooks/api/use-attr-for-references";
import { addReferences, deleteReference, getReferences, getSampleReferences } from "../../lib/referenceData";
import { sortByOrder } from "../../lib/helpers";

export const ReferencesContext = createContext({
    references: {
        activeProducts: [],
        activeProductKeys: [],
        products: [],
        refresh: false,
    },
});

export const ReferencesProvider = ({ children, codingWipKey, task, onHideControls }) => {
    const [modalSelect, setModalSelect] = useState(false);
    const [references, setReferences] = useState({
        activeProducts: [],
        activeProductKeys: [],
        products: [],
        refresh: false,
    });

    const {
        isSuccess: isAttributeReferencesFetched,
        isLoading: isAttributeReferencesLoading,
        data: attrDefinitions,
    } = useAttributesForReferences(codingWipKey, [...references.activeProductKeys]);

    useEffect(() => {
        fetchReferences();
    }, []);

    const fetchReferences = async () => {
        const refs = await getReferences(codingWipKey, task.key);
        const referenceProducts = refs.sort(sortByOrder);

        if (!referenceProducts || referenceProducts.length < 1) {
            const topReferences = await getSampleReferences(codingWipKey, task.upc);
            if (topReferences && topReferences.length > 0) {
                updateReferenceData(topReferences);
            }
        } else {
            const activeProducts = referenceProducts.filter((ref) => ref.order > 0);
            const activeProductKeys = activeProducts.map((r) => r.key);
            setReferences({
                activeProducts,
                activeProductKeys,
                products: referenceProducts,
                refresh: true,
            });
        }
    };

    const updateReferenceData = async (products) => {
        const refs = products.map((ref) => {
            return { pid: ref.key, _seq: ref.order.toString() === "0" ? null : ref.order };
        });
        const activeProducts = products.filter((ref) => ref.order > 0).sort(sortByOrder);
        const activeProductKeys = activeProducts.map((ref) => ref.key);

        const saveReferences = await addReferences(codingWipKey, { refs: refs, taskKey: task.key });
        if (!saveReferences) {
            return;
        }

        setReferences({
            activeProducts,
            activeProductKeys,
            products: products,
            refresh: true,
        });

        notify.success("References saved");

        setModalSelect(false);
        onHideControls(false);
    };

    const toggleModalSelect = () => {
        setModalSelect(!modalSelect);
        onHideControls(!modalSelect);
    };

    const selectStyles = modalSelect ? { display: "none" } : {};

    const selectReferences = modalSelect ? (
        <div>
            <SelectReferences
                updateReferences={updateReferenceData}
                references={references.products}
                deleteReference={deleteReference}
                wipKey={codingWipKey}
                taskKey={task.key}
            />
        </div>
    ) : null;

    return (
        <ReferencesContext.Provider
            value={{
                references,
                fetchReferences,
                attrDefinitions,
                isAttributeReferencesFetched,
                isAttributeReferencesLoading,
                selectStyles,
                modalSelect,
                setModalSelect,
                toggleModalSelect,
                updateReferenceData,
                deleteReference,
                selectReferences,
            }}
        >
            {children}
        </ReferencesContext.Provider>
    );
};
